import React, { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../utils/api';
import { isPosModuleEnabled } from '../config/featureFlags';
import '../App.css';

type PosSite = { siteId: string; label: string };
type PosStaff = {
  id: number;
  displayName: string;
  role: string;
  siteId: string;
  active: boolean;
  breweryUserEmail: string | null;
  hasPin: boolean;
  lockedUntil: string | null;
};
type PosDevice = {
  id: number;
  siteId: string;
  name: string;
  deviceType: string;
  active: boolean;
  lastSeenAt: string | null;
};

const ROLE_OPTIONS = [
  { value: 'PosServer', label: 'Server' },
  { value: 'PosBartender', label: 'Bartender' },
  { value: 'PosManager', label: 'Manager' },
];

const DEVICE_TYPE_OPTIONS = [
  { value: 'countertop', label: 'Countertop' },
  { value: 'handheld', label: 'Handheld' },
];

const emptyStaffForm = {
  displayName: '',
  role: 'PosServer',
  siteId: 'TAPROOM-MADISON',
  pin: '',
  breweryUserEmail: '',
};

const emptyDeviceForm = {
  name: '',
  siteId: 'TAPROOM-MADISON',
  deviceType: 'countertop',
};

const PosAdmin: React.FC = () => {
  const [sites, setSites] = useState<PosSite[]>([]);
  const [staff, setStaff] = useState<PosStaff[]>([]);
  const [devices, setDevices] = useState<PosDevice[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [staffForm, setStaffForm] = useState(emptyStaffForm);
  const [deviceForm, setDeviceForm] = useState(emptyDeviceForm);
  const [pinEditId, setPinEditId] = useState<number | null>(null);
  const [pinValue, setPinValue] = useState('');
  const [newDeviceToken, setNewDeviceToken] = useState<string | null>(null);

  const loadAll = useCallback(async () => {
    setError(null);
    try {
      const [statusRes, staffRes, devicesRes] = await Promise.all([
        apiFetch('/api/pos/status'),
        apiFetch('/api/pos/staff'),
        apiFetch('/api/pos/devices'),
      ]);
      const status = await statusRes.json();
      const staffRows = await staffRes.json();
      const deviceRows = await devicesRes.json();
      setSites(Array.isArray(status.sites) ? status.sites : []);
      setStaff(Array.isArray(staffRows) ? staffRows : []);
      setDevices(Array.isArray(deviceRows) ? deviceRows : []);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load POS admin data';
      setError(message);
    }
  }, []);

  useEffect(() => {
    if (isPosModuleEnabled()) {
      loadAll();
    }
  }, [loadAll]);

  if (!isPosModuleEnabled()) {
    return (
      <div className="page-container container">
        <h2>POS</h2>
        <p>The POS module is turned off. Set POS_MODULE=1 (and REACT_APP_POS_MODULE=1 for the web build) to enable it.</p>
      </div>
    );
  }

  const siteLabel = (siteId: string) =>
    sites.find((s) => s.siteId === siteId)?.label || siteId;

  const roleLabel = (role: string) =>
    ROLE_OPTIONS.find((r) => r.value === role)?.label || role;

  const handleCreateStaff = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setError(null);
    setSuccess(null);
    if (!staffForm.displayName.trim()) {
      setError('Staff display name is required');
      return;
    }
    try {
      setSaving(true);
      await apiFetch('/api/pos/staff', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          displayName: staffForm.displayName.trim(),
          role: staffForm.role,
          siteId: staffForm.siteId,
          pin: staffForm.pin.trim() || undefined,
          breweryUserEmail: staffForm.breweryUserEmail.trim() || null,
        }),
      });
      setStaffForm(emptyStaffForm);
      setSuccess('POS staff member added');
      await loadAll();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to add staff');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStaff = async (row: PosStaff) => {
    setError(null);
    setSuccess(null);
    try {
      await apiFetch(`/api/pos/staff/${row.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ active: !row.active }),
      });
      await loadAll();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update staff');
    }
  };

  const handleSetPin = async (staffId: number) => {
    setError(null);
    setSuccess(null);
    if (!/^\d{4,6}$/.test(pinValue.trim())) {
      setError('PIN must be 4–6 digits');
      return;
    }
    try {
      setSaving(true);
      await apiFetch(`/api/pos/staff/${staffId}/pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin: pinValue.trim() }),
      });
      setPinEditId(null);
      setPinValue('');
      setSuccess('PIN updated');
      await loadAll();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to set PIN');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateDevice = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setError(null);
    setSuccess(null);
    setNewDeviceToken(null);
    if (!deviceForm.name.trim()) {
      setError('Device name is required');
      return;
    }
    try {
      setSaving(true);
      const res = await apiFetch('/api/pos/devices', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: deviceForm.name.trim(),
          siteId: deviceForm.siteId,
          deviceType: deviceForm.deviceType,
        }),
      });
      const data = await res.json();
      setDeviceForm(emptyDeviceForm);
      setNewDeviceToken(data.deviceToken || null);
      setSuccess('Device registered — copy the device token now; it will not be shown again.');
      await loadAll();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to register device');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleDevice = async (row: PosDevice) => {
    setError(null);
    setSuccess(null);
    try {
      await apiFetch(`/api/pos/devices/${row.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ active: !row.active }),
      });
      await loadAll();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update device');
    }
  };

  const siteOptions =
    sites.length > 0
      ? sites
      : [
          { siteId: 'TAPROOM-MADISON', label: 'Madison' },
          { siteId: 'TAPROOM-ATHENS', label: 'Athens' },
        ];

  return (
    <div className="page-container container">
      <h2>POS Staff &amp; Devices</h2>
      <p className="text-muted">
        Manage restaurant floor logins (PIN) and registered POS terminals. Brewery email/password
        accounts are unchanged. Link a brewery login email only when someone needs both systems.
        After 10 missed PINs in a row, that staff login pauses for about a minute automatically —
        no admin unlock needed; a correct PIN also clears the miss counter.
      </p>

      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}
      {newDeviceToken && (
        <div className="alert alert-warning">
          <strong>New device token</strong> (copy now):
          <pre style={{ marginTop: 8, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{newDeviceToken}</pre>
        </div>
      )}

      <section style={{ marginBottom: 40 }}>
        <h3>Add staff</h3>
        <form onSubmit={handleCreateStaff} className="row g-2 align-items-end">
          <div className="col-md-3">
            <label className="form-label">Display name</label>
            <input
              className="form-control"
              value={staffForm.displayName}
              onChange={(e) => setStaffForm({ ...staffForm, displayName: e.target.value })}
              placeholder="Floor display name"
              autoComplete="off"
            />
          </div>
          <div className="col-md-2">
            <label className="form-label">Role</label>
            <select
              className="form-select"
              value={staffForm.role}
              onChange={(e) => setStaffForm({ ...staffForm, role: e.target.value })}
            >
              {ROLE_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-2">
            <label className="form-label">Site</label>
            <select
              className="form-select"
              value={staffForm.siteId}
              onChange={(e) => setStaffForm({ ...staffForm, siteId: e.target.value })}
            >
              {siteOptions.map((s) => (
                <option key={s.siteId} value={s.siteId}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-2">
            <label className="form-label">PIN (optional)</label>
            <input
              className="form-control"
              value={staffForm.pin}
              onChange={(e) => setStaffForm({ ...staffForm, pin: e.target.value })}
              placeholder="4–6 digits"
              inputMode="numeric"
              autoComplete="off"
            />
          </div>
          <div className="col-md-2">
            <label className="form-label">Brewery login (optional)</label>
            <input
              className="form-control"
              value={staffForm.breweryUserEmail}
              onChange={(e) => setStaffForm({ ...staffForm, breweryUserEmail: e.target.value })}
              placeholder="Existing brewery email"
              autoComplete="off"
            />
          </div>
          <div className="col-md-1">
            <button type="submit" className="btn btn-primary w-100" disabled={saving}>
              Add
            </button>
          </div>
        </form>

        <div className="table-responsive" style={{ marginTop: 16 }}>
          <table className="table table-sm table-striped">
            <thead>
              <tr>
                <th>Name</th>
                <th>Role</th>
                <th>Site</th>
                <th>PIN</th>
                <th>Brewery link</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {staff.map((row) => (
                <tr key={row.id}>
                  <td>{row.displayName}</td>
                  <td>{roleLabel(row.role)}</td>
                  <td>{siteLabel(row.siteId)}</td>
                  <td>
                    {pinEditId === row.id ? (
                      <span className="d-inline-flex gap-1">
                        <input
                          className="form-control form-control-sm"
                          style={{ width: 100 }}
                          value={pinValue}
                          onChange={(e) => setPinValue(e.target.value)}
                          placeholder="4–6 digits"
                          inputMode="numeric"
                          autoComplete="off"
                        />
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          disabled={saving}
                          onClick={() => handleSetPin(row.id)}
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => {
                            setPinEditId(null);
                            setPinValue('');
                          }}
                        >
                          Cancel
                        </button>
                      </span>
                    ) : (
                      <span>
                        {row.hasPin ? 'Set' : 'Not set'}
                        {row.lockedUntil ? ' (cooling down ~1 min)' : ''}{' '}
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0"
                          onClick={() => {
                            setPinEditId(row.id);
                            setPinValue('');
                          }}
                        >
                          {row.hasPin ? 'Reset' : 'Set'} PIN
                        </button>
                      </span>
                    )}
                  </td>
                  <td>{row.breweryUserEmail || '—'}</td>
                  <td>{row.active ? 'Active' : 'Inactive'}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => handleToggleStaff(row)}
                    >
                      {row.active ? 'Deactivate' : 'Activate'}
                    </button>
                  </td>
                </tr>
              ))}
              {staff.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-muted">
                    No POS staff yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h3>Register device</h3>
        <form onSubmit={handleCreateDevice} className="row g-2 align-items-end">
          <div className="col-md-4">
            <label className="form-label">Device name</label>
            <input
              className="form-control"
              value={deviceForm.name}
              onChange={(e) => setDeviceForm({ ...deviceForm, name: e.target.value })}
              placeholder="Terminal label"
              autoComplete="off"
            />
          </div>
          <div className="col-md-3">
            <label className="form-label">Site</label>
            <select
              className="form-select"
              value={deviceForm.siteId}
              onChange={(e) => setDeviceForm({ ...deviceForm, siteId: e.target.value })}
            >
              {siteOptions.map((s) => (
                <option key={s.siteId} value={s.siteId}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label">Type</label>
            <select
              className="form-select"
              value={deviceForm.deviceType}
              onChange={(e) => setDeviceForm({ ...deviceForm, deviceType: e.target.value })}
            >
              {DEVICE_TYPE_OPTIONS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-2">
            <button type="submit" className="btn btn-primary w-100" disabled={saving}>
              Register
            </button>
          </div>
        </form>

        <div className="table-responsive" style={{ marginTop: 16 }}>
          <table className="table table-sm table-striped">
            <thead>
              <tr>
                <th>Name</th>
                <th>Site</th>
                <th>Type</th>
                <th>Last seen</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {devices.map((row) => (
                <tr key={row.id}>
                  <td>{row.name}</td>
                  <td>{siteLabel(row.siteId)}</td>
                  <td>{row.deviceType}</td>
                  <td>{row.lastSeenAt || '—'}</td>
                  <td>{row.active ? 'Active' : 'Inactive'}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => handleToggleDevice(row)}
                    >
                      {row.active ? 'Deactivate' : 'Activate'}
                    </button>
                  </td>
                </tr>
              ))}
              {devices.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-muted">
                    No POS devices yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default PosAdmin;
